'use client';

/**
 * Customer password field for model 2 (`password_plus_factor`, #652).
 *
 * `enter` mode is a plain current-password field for the login page.
 * `create` mode (invitation landing) adds the client's rules as a live
 * checklist that is announced politely and does not rely on colour alone
 * (WCAG 2.2 AA). Both modes have a show/hide toggle.
 */

import { useId, useState } from 'react';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CustomerPasswordPolicy } from '@/types/organization';
import { describePolicy, evaluateCustomerPassword } from '@/lib/auth/customerPasswordPolicy';

export interface CustomerPasswordInputProps {
  mode: 'create' | 'enter';
  value: string;
  onChange: (value: string) => void;
  /** Required in `create` mode to drive the checklist. */
  policy?: CustomerPasswordPolicy;
  label?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

export default function CustomerPasswordInput(props: CustomerPasswordInputProps) {
  const { mode, value, onChange, policy, disabled, className } = props;
  const generatedId = useId();
  const id = props.id ?? `customer-password-${generatedId}`;
  const label = props.label ?? (mode === 'create' ? 'Create a password' : 'Password');
  const [visible, setVisible] = useState(false);
  const showChecklist = mode === 'create' && !!policy;
  const hintId = `${id}-hint`;
  const checklistId = `${id}-checklist`;

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          data-testid={`customer-password-${mode}`}
          type={visible ? 'text' : 'password'}
          autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          disabled={disabled}
          aria-describedby={showChecklist ? `${hintId} ${checklistId}` : undefined}
          className={`pr-11 ${className ?? 'h-11'}`}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          aria-controls={id}
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {visible ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        </button>
      </div>

      {showChecklist && (
        <>
          <p id={hintId} className="text-xs text-muted-foreground">
            {describePolicy(policy)}.
          </p>
          <ul
            id={checklistId}
            data-testid="customer-password-checklist"
            aria-live="polite"
            className="space-y-1 text-sm"
          >
            {evaluateCustomerPassword(value, policy).map((rule) => (
              <li
                key={rule.id}
                data-testid={`customer-password-rule-${rule.id}`}
                data-met={rule.met}
                className={`flex items-center gap-2 ${rule.met ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}`}
              >
                {rule.met ? (
                  <Check className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <X className="h-4 w-4" aria-hidden="true" />
                )}
                <span>
                  {rule.label}
                  <span className="sr-only">{rule.met ? ' – met' : ' – not met'}</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
